import { IInsightAccess, InsightType, SlotMetricsData } from 'common/common';
import { playerTypeEnum } from 'common/commonEnum';
import { InsightValue } from 'common/types';

export enum InsightIdEnum {
  MoneyWon = 1,
  Spins = 2,
  AvgWinPerSpins = 3,
  WinningSpins = 4,
  Jackpots = 5,
  NoOfJackpot = 6,
  MoneyPlayed = 7,
  RTP = 8,
  SLEEPSLOT = 9,
  VOL = 10,
  POP = 11,
  JackpotWinPerSpin = 12,
  NonJacpotWinPerSpin = 13,
}

export const allInsightKeys: InsightValue[] = [
  'return',
  'avg_money_won_per_spin',
  'money_played',
  'money_won',
  'spins',
  'win_rate',
  'jackpots',
  'jackpot_value',
  'sleeper_slot_rating',
  'volatility_rating',
  'pop',
  'non_jkpt_won_per_spin',
  'jackpot_value_per_spin',
];

export enum InsightValueEnum {
  return = 'return',
  avg_money_won_per_spin = 'avg_money_won_per_spin',
  money_played = 'money_played',
  money_won = 'money_won',
  spins = 'spins',
  win_rate = 'win_rate',
  jackpots = 'jackpots',
  jackpot_value = 'jackpot_value',
  sleeper_slot_rating = 'sleeper_slot_rating',
  volatility_rating = 'volatility_rating',
  pop = 'pop',
  non_jkpt_won_per_spin = 'non_jkpt_won_per_spin',
  jackpot_value_per_spin = 'jackpot_value_per_spin',
}

export const insightAccessList: IInsightAccess[] = [
  {
    value: InsightValueEnum.return,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  {
    value: InsightValueEnum.avg_money_won_per_spin,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  {
    value: InsightValueEnum.money_played,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  {
    value: InsightValueEnum.money_won,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  {
    value: InsightValueEnum.spins,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  {
    value: InsightValueEnum.win_rate,
    playerTypeAccess: [playerTypeEnum.slotCheckPro],
  },
  {
    value: InsightValueEnum.jackpots,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  {
    value: InsightValueEnum.jackpot_value,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  { value: InsightValueEnum.sleeper_slot_rating, playerTypeAccess: [playerTypeEnum.slotCheckPro] },
  {
    value: InsightValueEnum.volatility_rating,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  { value: InsightValueEnum.pop, playerTypeAccess: [playerTypeEnum.slotCheckPro] },
  {
    value: InsightValueEnum.non_jkpt_won_per_spin,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
  {
    value: InsightValueEnum.jackpot_value_per_spin,
    playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
  },
];

export const InsightsItems: Omit<InsightType, 'playerTypeAccess'>[] = [
  {
    id: 1,
    value: InsightValueEnum.return,
    field: 'RPT',
    label: 'Return to Player',
    enableDarkBg: false,
    trendLabel: 'RTP',
    hotColdLabel: 'percent returned to player',
    insightFieldsSelectorLabel: 'RTP',
    graphLabel: '% RTP',
    short_name: 'RTP',
    color: '#b35fdb',
    backgroundColor: '#be64e7',
    className: '',
    description: '% of money won divided by money played',
    display_order: 1,
    insightId: InsightIdEnum.RTP,
    icon: '%',
    includeInTrends: true,
    orderInTrends: 1,
    graphDescription: '% of money played that was returned to player.',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 1,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'RTP (bars)',
        axes: 'right',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: false,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'RTP',
    enableMainPullDown: true,
    compareEnabled: false,
    defaultCompareSelected: false,
    compareLabel: 'RTP'
  },
  {
    id: 2,
    value: InsightValueEnum.avg_money_won_per_spin,
    field: 'avg_money_won_per_spin',
    label: 'Win per Spin',
    enableDarkBg: true,
    short_name: 'W/S',
    trendLabel: 'W/S',
    insightFieldsSelectorLabel: 'W/S',
    graphLabel: 'W/S',
    hotColdLabel: 'wins per spin',
    color: '#bf9ed2',
    backgroundColor: '#ac88c1',
    className: '',
    description: 'Average amount of money won per spin',
    display_order: 7,
    insightId: InsightIdEnum.AvgWinPerSpins,
    icon: '$',
    includeInTrends: true,
    orderInTrends: 6,
    graphDescription: 'Average amount of money won per spin',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 7,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'W/S (bars)',
        axes: 'right',
        value: InsightValueEnum.avg_money_won_per_spin,
        vaultFormat: 'money',
        valueFormatSymbol: '$',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'W/S',
    enableMainPullDown: true,
    compareEnabled: false,
    defaultCompareSelected: false,
    compareLabel: 'W/S'
  },
  {
    id: 3,
    value: InsightValueEnum.money_played,
    field: 'money_played',
    label: 'Total Play', //oin-in
    enableDarkBg: false,
    short_name: 'Total Play', //Coin-in
    trendLabel: 'Play', //C-in
    hotColdLabel: 'Total Play', // Coin-in
    insightFieldsSelectorLabel: 'Total Play',// Coin-in
    graphLabel: '$ Total Play', // $ Coin-in
    color: '#00e953',
    backgroundColor: '#18c355',
    className: '',
    description: 'Total money played',
    display_order: 4,
    insightId: InsightIdEnum.MoneyPlayed,
    icon: '$',
    includeInTrends: true,
    orderInTrends: 4,
    graphDescription: 'Total Play compared to RTP', // Total Coin-in compared to RTP
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 4,
    graphData: [
      {
        group: 'coin-in-out',
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        group: 'coin-in-out',
        icon: 'bar',
        name: 'Total Play (bars)',// Coin-in (bars)
        axes: 'right',
        value: InsightValueEnum.money_played,
        vaultFormat: 'money',
        valueFormatSymbol: '$',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'Play', // C-in
    enableMainPullDown: true,
    compareEnabled: false,
    defaultCompareSelected: false,
    // Explore drawer / Compare chip (InsightInline); EGM Details keep label/short_name.
    compareLabel: '$ Played'
  },
  {
    id: 4,
    value: InsightValueEnum.money_won,
    field: 'money_won',
    label: 'Total Win', // Coin-out
    enableDarkBg: false,
    short_name: 'Total Win',
    trendLabel: 'Win', // C-out
    hotColdLabel: 'Total Win', // 'Coin-out'
    insightFieldsSelectorLabel: 'Total Win', // 'Coin-out'
    graphLabel: '$ Total Win', // '$ Coin-out'
    color: '#7384ff',
    backgroundColor: '#7384ff',
    className: '',
    description: 'Total amount of money the slot has paid out irrespective of amount paid in',
    display_order: 3,
    insightId: InsightIdEnum.MoneyWon,
    icon: '$',
    includeInTrends: true,
    orderInTrends: 3,
    graphDescription: 'TotalW Win compared to RTP', // 'Total Coin-out compared to RTP'
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 3,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'Total Win (bars)', // 'Coin-out (bars)'
        axes: 'right',
        value: InsightValueEnum.money_won,
        vaultFormat: 'money',
        valueFormatSymbol: '$',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'Win',
    enableMainPullDown: true,
    compareEnabled: true,
    defaultCompareSelected: false,
    // Explore drawer / Compare chip (InsightInline); EGM Details keep label/short_name.
    compareLabel: '$ Won'
  },
  {
    id: 5,
    value: InsightValueEnum.spins,
    field: 'spins',
    // Selectable insight pill / chip label (Explore, Compare, graph tabs).
    // Sort headers, trends, and value labels keep short_name / insightFieldsSelectorLabel "Spins".
    label: 'Popular',
    short_name: 'Spins',
    trendLabel: 'Spins',
    hotColdLabel: 'total spins',
    insightFieldsSelectorLabel: 'Spins',
    graphLabel: '$ Spins',
    color: '#ff6a4b',
    backgroundColor: '#ff6a4b',
    enableDarkBg: false,
    className: '',
    description: 'Total number of spins',
    display_order: 5,
    insightId: InsightIdEnum.Spins,
    icon: '#',
    includeInTrends: true,
    orderInTrends: 5,
    graphDescription: 'Total number of spins',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 6,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'Spins (bars)',
        axes: 'right',
        value: InsightValueEnum.spins,
        vaultFormat: 'number',
        valueFormatSymbol: '',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'Popular',
    enableMainPullDown: true,
    compareEnabled: true,
    defaultCompareSelected: false,
    compareLabel: 'Popular',
  },
  // {
  //   id: 6,
  //   value: 'win_rate',
  //   field: 'win_rate',
  //   label: 'Winning Spins',
  //   enableDarkBg: true,
  //   short_name: '% WS',
  //   trendLabel: '% WS',
  //   hotColdLabel: '% winning spins',
  //   insightFieldsSelectorLabel: '% WS',
  //   color: '#deb084',
  //   backgroundColor: '#d8aa7f',
  //   className: '',
  //   description: '% of spins that won money',
  //   display_order: 9,
  //   insightId: InsightIdEnum.WinningSpins,
  //   icon: '%',
  //   playerTypeAccess: [playerTypeEnum.slotCheckPro],
  // },
  {
    id: 7,
    value: InsightValueEnum.jackpots,
    field: 'jackpots',
    label: '# Jackpots',
    enableDarkBg: true,
    trendLabel: '# JKPT',
    hotColdLabel: 'money paid out in jackpots',
    insightFieldsSelectorLabel: '$ | # JKPT',
    graphLabel: '$ | # JKPT',
    short_name: 'Jackpots',
    color: '#22af6a',
    backgroundColor: '#00a052',
    className: '',
    description: 'Total number of jackpots that occurred',
    display_order: 20,
    insightId: InsightIdEnum.NoOfJackpot,
    icon: '#',
    includeInTrends: false,
    orderInTrends: 0,
    graphDescription: 'Total number of jackpots that occurred',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: false,
    enableGraphButtonOrder: 6,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: '# JKPT (bars)',
        axes: 'right',
        value: InsightValueEnum.jackpots,
        vaultFormat: 'number',
        valueFormatSymbol: '',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'RTP/JKPT',
    enableMainPullDown: true,
    compareEnabled: false,
    defaultCompareSelected: false,
    compareLabel: 'Jackpots'
  },
  {
    id: 8,
    value: InsightValueEnum.jackpot_value,
    field: 'jackpot_value',
    label: 'Jackpots (JKPT)',
    enableDarkBg: true,
    short_name: 'Jackpots',
    trendLabel: 'JKPT',
    hotColdLabel: 'money paid out in jackpots',
    insightFieldsSelectorLabel: '$ | # JKPT',
    graphLabel: '$ | # JKPT',
    color: '#8fffba',
    backgroundColor: '#68c38a',
    className: '',
    description: 'Total amount of money won from jackpots',
    display_order: 2,
    insightId: InsightIdEnum.Jackpots,
    icon: '$',
    includeInTrends: true,
    orderInTrends: 2,
    graphDescription: 'Total amount of money won from jackpots',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 2,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'JKPT (bars)',
        axes: 'right',
        value: InsightValueEnum.jackpot_value,
        vaultFormat: 'money',
        valueFormatSymbol: '$',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'JKPT',
    enableMainPullDown: true,
    compareEnabled: false,
    defaultCompareSelected: false,
    compareLabel: 'Jackpots'
  },
  {
    id: 10,
    value: InsightValueEnum.volatility_rating,
    field: 'volatility_rating',
    label: 'Volatility',
    enableDarkBg: false,
    short_name: 'VOL',
    trendLabel: 'VOL',
    hotColdLabel: 'relative volatility',
    highVoltilityLabel: 'Volatility Rating (High)',
    lowVoltilityLabel: 'Volatility Rating (Low)',
    insightFieldsSelectorLabel: 'VOL',
    graphLabel: 'VOL',
    color: '#aba170',
    backgroundColor: '#988f60',
    className: '',
    description: 'Relatively high % (or low %) returned to player and relatively low % (or high %) winning spins',
    display_order: 4,
    insightId: InsightIdEnum.VOL,
    icon: '^',
    includeInTrends: false,
    orderInTrends: 0,
    graphDescription: 'Relatively high % (or low %) returned to player and relatively low % (or high %) winning spins',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheckPro],
    enableInsight: false,
    enableGraph: false,
    enableGraphPulldown: false,
    graphPulldownOrder: 0,
    enableGraphButton: false,
    enableGraphButtonOrder: 8,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'VOL (bars)',
        axes: 'right',
        value: InsightValueEnum.volatility_rating,
        vaultFormat: 'number',
        valueFormatSymbol: '',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'VOL',
    enableMainPullDown: false,
    compareEnabled: false,
    defaultCompareSelected: false,
    compareLabel: 'VOL'
  },
  {
    id: 11,
    value: InsightValueEnum.pop,
    field: 'pop',
    label: 'Payout To Player (POP)',
    short_name: 'POP',
    trendLabel: 'POP',
    hotColdLabel: 'percent paid out to player',
    insightFieldsSelectorLabel: 'POP',
    graphLabel: 'POP',
    color: '#aba170',
    backgroundColor: '#988f60',
    enableDarkBg: false,
    className: '',
    description: 'None', // To be added
    display_order: 1,
    insightId: InsightIdEnum.POP,
    icon: '^', // To be added
    includeInTrends: false,
    orderInTrends: 0,
    graphDescription: 'Total payout percentages % returned to player',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: false,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: false,
    enableGraphButtonOrder: 9,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'POP (bars)',
        axes: 'right',
        value: InsightValueEnum.pop,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'POP',
    enableMainPullDown: false,
    compareEnabled: false,
    defaultCompareSelected: false,
    compareLabel: 'POP'
  },
  {
    id: 14,
    value: InsightValueEnum.non_jkpt_won_per_spin,
    field: 'non_jkpt_won_per_spin',
    label: 'Non-Jackpot Win per Spin (W/S-J)',
    enableDarkBg: true,
    short_name: 'W/S-J',
    trendLabel: 'W/S-J',
    hotColdLabel: '$ Non-Jackpot Win per Spin',
    insightFieldsSelectorLabel: 'W/S-J',
    graphLabel: 'W/S-J',
    color: '#deb084',
    backgroundColor: '#d8aa7f',
    className: '',
    description: 'Non Jackpot win per spin',
    display_order: 8,
    insightId: InsightIdEnum.NonJacpotWinPerSpin,
    icon: '$',
    includeInTrends: true,
    orderInTrends: 7,
    graphDescription: 'Win per spin that do not include jackpots',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 8,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'W/S-J (bars)',
        axes: 'right',
        value: InsightValueEnum.non_jkpt_won_per_spin,
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'W/S-J',
    enableMainPullDown: true,
    compareEnabled: true,
    defaultCompareSelected: false,
    compareLabel: 'Non-jackpot Win/Spin (W/S-J)'
  },
  {
    id: 15,
    value: InsightValueEnum.jackpot_value_per_spin,
    field: 'jackpot_value_per_spin',
    label: 'Jackpot Win per Spin',
    enableDarkBg: true,
    short_name: 'J/S',
    trendLabel: 'J/S',
    hotColdLabel: '$ Jackpot Win per Spin',
    insightFieldsSelectorLabel: 'J/S',
    graphLabel: 'J/S',
    color: '#deb084',
    backgroundColor: '#d8aa7f',
    className: '',
    description: 'Jackpot win per spin',
    display_order: 8,
    insightId: InsightIdEnum.JackpotWinPerSpin,
    icon: '$',
    includeInTrends: true,
    orderInTrends: 8,
    graphDescription: 'Jackpot win per spin',
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 9,
    graphData: [
      {
        icon: 'line',
        name: 'RTP (line)',
        axes: 'left',
        value: InsightValueEnum.return,
        vaultFormat: 'percent',
        valueFormatSymbol: '%',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'J/S (bars)',
        axes: 'right',
        value: InsightValueEnum.jackpot_value_per_spin,
        vaultFormat: 'money',
        valueFormatSymbol: '$',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'J/S',
    enableMainPullDown: true,
    compareEnabled: true,
    defaultCompareSelected: false,
    compareLabel: 'Jackpot Win/Spin (JW/S)'
    
  },
  {
    id: 16,
    value: InsightValueEnum.money_played,
    field: 'money_played',
    label: 'Total Play/Total Win', // 'Coin-in/Coin-out'
    enableDarkBg: false,
    short_name: 'Play/Win', // 'Coin In/Out'
    trendLabel: 'Play/Win', // 'Coin In/Out'
    hotColdLabel: 'Total Play', // 'Coin-in'
    insightFieldsSelectorLabel: 'Total Play', // 'Coin-in'
    graphLabel: '$ Total Play', // '$ Coin-in'
    color: '#00e953',
    backgroundColor: '#18c355',
    className: '',
    description: 'Total money played',
    display_order: 6,
    insightId: InsightIdEnum.MoneyPlayed,
    icon: '$',
    includeInTrends: false,
    orderInTrends: 4,
    graphDescription: 'Total Total Play compared to Total Win', // 'Total Coin-in compared to Coin-out'
    graphPlayerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
    enableInsight: true,
    enableGraph: true,
    enableGraphPulldown: false,
    graphPulldownOrder: 1,
    enableGraphButton: true,
    enableGraphButtonOrder: 5,
    graphData: [
      {
        icon: 'line',
        name: 'Total Play (line)', // 'Coin-in (line)'
        axes: 'left',
        value: InsightValueEnum.money_played,
        vaultFormat: 'money',
        valueFormatSymbol: '$',
        displayAxesText: true,
        isPrimary: true,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
      {
        icon: 'bar',
        name: 'Total Win (bars)', // 'Coin-out (bars)'
        axes: 'right',
        value: InsightValueEnum.money_won,
        vaultFormat: 'money',
        valueFormatSymbol: '$',
        displayAxesText: true,
        isPrimary: false,
        playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro],
      },
    ],
    graphButtonName: 'Play/Win', // 'Coin In/Out'
    enableMainPullDown: false,
    compareEnabled: true,
    defaultCompareSelected: true,
    compareLabel: 'Total Played (Played)' // 'Coin-in'
  },
];

export const Insights: InsightType[] = InsightsItems.map((item) => {
  const access = insightAccessList.find((a) => a.value === item.value);
  if (!access) {
    throw new Error(`Missing playerTypeAccess for timeframe: ${item.value}`);
  }

  return {
    ...item,
    playerTypeAccess: access.playerTypeAccess,
  };
});

export const myJackpotsPageInsights = Insights.map((insightInfo) =>
  insightInfo.value === InsightValueEnum.jackpot_value
    ? { ...insightInfo, playerTypeAccess: [playerTypeEnum.slotCheck, playerTypeEnum.slotCheckPro] }
    : insightInfo
);

export const hotSlotsLabels: any = {
  spins: {
    label: 'Popular', // selectable pill; value/sort labels stay "Spins"
    secondaryLabel: 'Spins',
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#F34D2B',
    is_show_trophy: true,
  },
  jackpots: {
    label: 'Jackpots (#J)',
    secondaryLabel: 'Jackpots (#J)',
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#FFF',
    is_show_trophy: true,
  },
  jackpot_value: {
    label: 'Jackpots (JKPT)',
    secondaryLabel: 'Jackpots (JKPT)',
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#089E3D',
    is_show_trophy: true,
  },
  money_won: {
    label: 'Total Win (Win)', // 'Coin-out (C-out)'
    secondaryLabel: 'Total Win (Win))', // 'Coin-out (C-out)'
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#089E3D',
    is_show_trophy: true,
  },
  money_played: {
    label: 'Total Play (Play)', // 'Coin-in (C-in)'
    secondaryLabel: 'Total Play (Play)', // 'Coin-in (C-in)'
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#089E3D',
    is_show_trophy: false,
  },
  win_rate: {
    label: 'Winning Spins (% WS)',
    secondaryLabel: '% WS',
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#DBD046',
    is_show_trophy: false,
  },
  return: {
    label: 'Return To Player (RTP)',
    secondaryLabel: 'RTP',
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#DBD046',
    is_show_trophy: false,
  },
  avg_money_won_per_spin: {
    label: 'Win/Spin (W/S) ',
    secondaryLabel: 'Win/Spin (W/S) ',
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#089E3D',
    is_show_trophy: true,
  },
  non_jkpt_won_per_spin: {
    label: 'Non-jackpot Win/Spin (W/S-J) ',
    secondaryLabel: 'Non-jackpot Win/Spin (W/S-J) ',
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#089E3D',
    is_show_trophy: true,
  },
  jackpot_value_per_spin: {
    label: 'Jackpot Win/Spin (J/S)',
    secondaryLabel: 'Jackpot Win/Spin (J/S)',
    asset_type: 'text',
    text: '',
    icon: '',
    color: '#089E3D',
    is_show_trophy: true,
  },
  sleeper_slot_rating: {
    label: 'Sleeper Slots',
    secondaryLabel: 'SS',
    asset_type: 'text',
    text: '',
    icon: 'assets/images/sleeper-slots.svg',
    color: '#1D89A5',
    is_show_trophy: false,
  },
  volatility_rating: {
    label: 'Volatility (VOL)',
    secondaryLabel: 'Volatility (VOL)',
    asset_type: 'text',
    text: '',
    icon: 'assets/images/volatility.svg',
    color: '#0A2D8B',
    is_show_trophy: false,
  },
  // time_played: {
  //   label: 'Time Played',
  //   secondaryLabel: 'Time Played',
  //   color: '#8FE3E8',
  //   is_show_trophy: false,
  //   asset_type: 'text',
  //   icon: 'assets/images/time-outline.svg',
  //   text: '',
  // },
  // session_played: {
  //   label: 'Sessions',
  //   secondaryLabel: 'Sessions',
  //   color: '#8FE3E8',
  //   icon: 'assets/images/hourglass-outline.svg',
  //   text: '',
  //   asset_type: 'text',
  //   is_show_trophy: false,
  // },
  pop: {
    label: 'Payout To Player (POP)',
    secondaryLabel: 'POP',
    color: '#8FE3E8',
    icon: 'assets/images/hourglass-outline.svg',
    text: '',
    asset_type: 'text',
    is_show_trophy: false,
  },
};

export const snapshots: SlotMetricsData = {
  snapShotArr: [
    {
      display_order: 1,
      className: 'snap-gray-blue',
      items: [
        {
          id: 1,
          field: 'pop',
          value: InsightValueEnum.pop,
          label: '% Payout to Player',
          snapshot_label: 'POP',
          short_name: 'POP',
          description: 'Total payout percentages % returned to player',
        },
        {
          id: 2,
          field: 'return',
          value: InsightValueEnum.return,
          label: '% Return to Player',
          snapshot_label: 'Return To Player (RTP)',
          short_name: 'RTP',
          description: '% of money won divided by money played',
        },
        {
          id: 3,
          field: 'spins',
          value: InsightValueEnum.spins,
          label: '# Spins',
          snapshot_label: 'Spins',
          short_name: 'Spins',
          description: 'Total number of spins',
        },
        {
          id: 4,
          field: 'volatility_rating',
          value: InsightValueEnum.volatility_rating,
          label: '^ Volatility',
          snapshot_label: 'Volatility (VOL)',
          short_name: 'Pers',
          description:
            'Relatively high % (or low %) returned to player and relatively low % (or high %) winning spins',
        },
      ],
    },
    {
      display_order: 2,
      className: 'snap-light-blue',
      items: [
        {
          id: 5,
          field: 'jackpot_value',
          value: InsightValueEnum.jackpot_value,
          label: '$ Jackpots',
          snapshot_label: 'Jackpots ',
          short_name: 'Jpots',
          description: 'Total amount of money won from jackpots',
        },
      ],
    },
    {
      display_order: 2,
      className: 'snap-gray-blue',
      items: [
        {
          id: 6,
          value: InsightValueEnum.money_won,
          field: 'money_won',
          label: '$ Total Win', // '$ Coin out'
          snapshot_label: 'Total Win', // 'Coin-out'
          short_name: 'Total Win', // 'Coin-out'
          description: 'Total amount of money the slot has paid out irrespective of amount paid in',
        },
        {
          id: 7,
          value: InsightValueEnum.money_played,
          field: 'money_played',
          label: '$ Total Play', // '$ Coin in'
          snapshot_label: 'Total Play', // 'Coin-in'
          short_name: 'Total Play', // 'Coin-in'
          description: 'Total money played',
        },
      ],
    },
    {
      display_order: 3,
      className: 'snap-light-blue',
      items: [
        {
          id: 8,
          value: InsightValueEnum.avg_money_won_per_spin,
          field: 'avg_money_won_per_spin',
          label: '$ Win per Spin',
          snapshot_label: 'Win / Spin (W/S)',
          short_name: 'W/S',
          description: 'Average amount of money won per spin',
        },
        {
          id: 13,
          value: InsightValueEnum.jackpot_value_per_spin,
          field: 'jackpot_value_per_spin',
          label: '$ Jackpot Win per Spin',
          snapshot_label: 'Jackpot Win / Spin (J/S)',
          short_name: '$ J/S',
          description: 'Jackpot win per spin',
        },
        {
          id: 12,
          value: InsightValueEnum.non_jkpt_won_per_spin,
          field: 'non_jkpt_won_per_spin',
          label: '$ Non-Jackpot Win per Spin',
          snapshot_label: 'Non-Jackpot Win / Spin (W/S-J)',
          short_name: 'W/S-J',
          description: 'Non Jackpot win per spin',
        },
        // {
        //   id: 9,
        //   value: 'win_rate',
        //   field: 'win_rate',
        //   label: '% Winning Spins',
        //   snapshot_label: 'Winning Spins (% WS)',
        //   short_name: '% WS',
        //   description: '% of spins that won money',
        // },
        // {
        //     id: 10,
        //     value: 'sleeper_slot_rating',
        //     field: 'sleeper_slot_rating',
        //     label: 'Z Sleeper Slots',
        //     snapshot_label: 'Sleeper Slots (1-6)',
        //     short_name: 'SS',
        //     description:
        //         'Slots that have a relatively high % returned to player with relatively few spins',
        // }
      ],
    },
  ],
};

export const myPlayInsights = [
  // {
  //   id: 11,
  //   value: 'time_played',
  //   field: 'time_played',
  //   label: 'Time Played',
  //   color: '#8FE3E8',
  //   backgroundColor: '#8FE3E8',
  //   className: '',
  //   description: 'Time Played',
  //   display_order: 11,
  //   insightId: 11,
  // },
  // {
  //   id: 12,
  //   value: 'session_played',
  //   field: 'session_played',
  //   label: 'Sessions',
  //   color: '#8FE3E8',
  //   backgroundColor: '#8FE3E8',
  //   className: '',
  //   description: 'Sessions',
  //   display_order: 12,
  //   insightId: 12,
  // },
];

export const insightValues = Insights.map((insight) => insight.value);

// Insight values that are valid choices for the compare "3rd insight" column.
export const compareEnabledInsightValues: string[] = Insights.filter(
  (insight) => insight.compareEnabled
).map((insight) => insight.value);

// The fallback 3rd insight ($ Played) used when the explore-page insight is not
// compare-supported (e.g. RTP / Hottest / Coldest, Jackpots, etc.).
export const defaultCompareInsightValue: string =
  Insights.find((insight) => insight.defaultCompareSelected)?.value ??
  InsightValueEnum.money_played;

// Resolve the 3rd compare insight from the explore-page insight: use the explore
// insight when it is compare-supported, otherwise fall back to $ Played.
export const resolveCompareInsight = (exploreInsight?: string | null): string =>
  exploreInsight && compareEnabledInsightValues.includes(exploreInsight)
    ? exploreInsight
    : defaultCompareInsightValue;

// Columns compare always renders alongside the selectable 3rd insight. When the
// explore insight is one of these (RTP for Hottest/Coldest, or Jackpots) it
// can't be the 3rd insight — that falls back to $ Played — but we still sort
// compare by it to mirror the explore view.
export const alwaysPresentCompareColumns: string[] = [
  InsightValueEnum.return,
  InsightValueEnum.jackpot_value,
];

// Resolve the compare sort column from the explore-page insight: sort by the
// explore insight when it is an always-present compare column, otherwise sort by
// the loaded 3rd insight.
export const resolveCompareSortBy = (exploreInsight?: string | null): string =>
  exploreInsight && alwaysPresentCompareColumns.includes(exploreInsight)
    ? exploreInsight
    : resolveCompareInsight(exploreInsight);

// compareOrder defines ordering for compare view. Lower numbers are rendered first.
export const compareOrder: { [key in InsightValueEnum]?: number } = {
  [InsightValueEnum.spins]: 1, // Spins first
  [InsightValueEnum.money_won]: 2, // Total Win
  [InsightValueEnum.money_played]: 3, // Total Played
  [InsightValueEnum.non_jkpt_won_per_spin]: 4, // W/S-J
  [InsightValueEnum.jackpot_value_per_spin]: 5, // JW/S
};
